// TipMessage.js
import Vue from 'vue'

// 定义消息类型配置
const typeConfig = {
  info: {
    color: '#111111',
    icon: 'ℹ'
  },
  error: {
    color: '#FF3300',
    icon: ''
  },
  success: {
    color: '#61E249',
    icon: ''
  }
}

// 创建消息组件
const MessageComponent = Vue.extend({
  render(h) {
    return h(
      'div',
      {
        id: "tip-message",
        class: ['message-container', `message-${this.type}`],
        style: {
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          display: this.visible ? 'flex' : 'none',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          opacity: this.visible ? 1 : 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: this.visible ? 'auto' : 'none'
        },
        on: {
          click: this.handleOverlayClick
        }
      },
      [
        h(
          'div',
          {
            class: 'message-content',
            style: {
              backgroundColor: '#ffffff',
              borderRadius: '10px',
              padding: '20px 40px',
              maxWidth: '80%',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              transform: this.visible ? 'scale(1)' : 'scale(0.9)',
              opacity: this.visible ? 1 : 0,
              transition: 'all 0.3s ease'
            },
            on: {
              click: (e) => e.stopPropagation()
            }
          },
          [
            // 消息文本
            h(
              'div',
              {
                style: {
                  color: this.currentColor,
                  fontSize: '14px',
                  fontFamily: 'PingFang SC',
                  fontWeight: 700,
                  lineHeight: '1.5',
                  wordBreak: 'break-word',
                  transition: 'color 0.3s ease'
                }
              },
              this.content
            ),
            // 关闭按钮
            h(
              'button',
              {
                style: {
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#999',
                  width: '24px',
                  height: '24px',
                  lineHeight: '24px',
                  padding: 0,
                  borderRadius: '50%',
                  display: this.showClose ? 'block' : 'none',
                  opacity: this.visible ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                },
                on: {
                  click: (e) => {
                    e.stopPropagation()
                    this.close()
                  }
                }
              },
              '×'
            )
          ]
        )
      ]
    )
  },
  props: {
    content: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['info', 'error', 'success'].includes(value)
    },
    duration: {
      type: Number,
      default: 3000
    },
    showClose: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      timer: null,
      currentColor: typeConfig.info.color
    }
  },
  watch: {
    type(newType) {
      this.currentColor = typeConfig[newType]?.color || typeConfig.info.color
    },
    content() {
      // 内容变化时重置定时器
      this.resetTimer()
    }
  },
  mounted() {
    this.currentColor = typeConfig[this.type]?.color || typeConfig.info.color
    this.show()
  },
  methods: {
    show() {
      this.visible = true
      this.setAutoClose()
    },
    
    hide() {
      this.visible = false
      this.clearTimer()
    },
    
    update(options) {
      const { content, type = 'info', duration = 3000, showClose = false } = options
      
      // 更新属性
      this.content = content
      this.type = type
      this.duration = duration
      this.showClose = showClose
      
      // 更新颜色
      this.currentColor = typeConfig[type]?.color || typeConfig.info.color
      
      // 重置定时器
      this.resetTimer()
      
      return this
    },
    
    setAutoClose() {
      this.clearTimer()
      if (this.duration > 0) {
        this.timer = setTimeout(() => {
          this.close()
        }, this.duration)
      }
    },
    
    resetTimer() {
      this.clearTimer()
      if (this.duration > 0) {
        this.timer = setTimeout(() => {
          this.close()
        }, this.duration)
      }
    },
    
    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
    },
    
    handleOverlayClick(event) {
      if (event.target.classList.contains('message-container')) {
        this.close()
      }
    },
    
    close() {
      this.hide()
      setTimeout(() => {
        this.$destroy()
        if (this.$el && this.$el.parentNode) {
          this.$el.parentNode.removeChild(this.$el)
        }
      }, 300)
    }
  },
  beforeDestroy() {
    this.clearTimer()
  }
})

// 定义动画样式
const style = document.createElement('style')
style.textContent = `
  .message-close {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #999;
    width: 24px;
    height: 24px;
    line-height: 24px;
    padding: 0;
    border-radius: 50%;
  }
  
  .message-close:hover {
    background-color: #f5f5f5;
    color: #666;
  }
`
document.head.appendChild(style)

// 消息管理器
class MessageManager {
  constructor() {
    this.currentInstance = null
    this.isAnimating = false
  }

  // 创建或更新消息实例
  createMessage(options) {
    const { content, type = 'info', duration = 3000, showClose = false } = options

    // 如果已有实例，则更新内容而不是重新创建
    if (this.currentInstance && !this.isAnimating) {
      return this.currentInstance.update({ content, type, duration, showClose })
    }
    
    // 如果没有实例或正在动画中，则创建新实例
    if (this.currentInstance && this.isAnimating) {
      // 如果正在关闭动画，先快速完成关闭动画
      this.currentInstance.hide()
      setTimeout(() => {
        this.currentInstance.$destroy()
        if (this.currentInstance.$el && this.currentInstance.$el.parentNode) {
          this.currentInstance.$el.parentNode.removeChild(this.currentInstance.$el)
        }
        this.currentInstance = null
        this.isAnimating = false
        this.createNewInstance(options)
      }, 50)
    } else {
      this.createNewInstance(options)
    }
  }
  
  createNewInstance(options) {
    const { content, type = 'info', duration = 3000, showClose = false } = options
    
    const instance = new MessageComponent({
      propsData: {
        content,
        type,
        duration,
        showClose
      }
    })

    const vm = instance.$mount()
    document.body.appendChild(vm.$el)
    
    // 监听实例的关闭事件
    vm.$on('hook:beforeDestroy', () => {
      this.currentInstance = null
      this.isAnimating = false
    })

    // 存储当前实例引用
    this.currentInstance = vm
    this.isAnimating = false

    return {
      close: () => {
        this.isAnimating = true
        vm.close()
      },
      update: (newOptions) => this.updateMessage(newOptions)
    }
  }
  
  // 更新现有消息
  updateMessage(options) {
    if (this.currentInstance && !this.isAnimating) {
      return this.currentInstance.update(options)
    }
    return this.createMessage(options)
  }

  // 正常提示
  info(content, duration = 3000) {
    return this.createMessage({
      content,
      type: 'info',
      duration
    })
  }

  // 错误提示
  error(content, duration = 3000) {
    return this.createMessage({
      content,
      type: 'error',
      duration
    })
  }

  // 成功提示
  success(content, duration = 3000) {
    return this.createMessage({
      content,
      type: 'success',
      duration
    })
  }

  // 手动隐藏当前消息
  hide() {
    if (this.currentInstance && !this.isAnimating) {
      this.isAnimating = true
      this.currentInstance.close()
    }
  }
  
  // 立即隐藏（无动画）
  hideImmediate() {
    if (this.currentInstance) {
      this.currentInstance.clearTimer()
      this.currentInstance.$destroy()
      if (this.currentInstance.$el && this.currentInstance.$el.parentNode) {
        this.currentInstance.$el.parentNode.removeChild(this.currentInstance.$el)
      }
      this.currentInstance = null
      this.isAnimating = false
    }
  }
}

// 创建单例
let messageManagerInstance = null

// 获取消息管理器实例
function getMessageManager() {
  if (!messageManagerInstance) {
    messageManagerInstance = new MessageManager()
  }
  return messageManagerInstance
}

// 导出API
const TipMessage = {
  // 显示正常提示
  info(content, duration = 3000) {
    return getMessageManager().info(content, duration)
  },

  // 显示错误提示
  error(content, duration = 3000) {
    return getMessageManager().error(content, duration)
  },

  // 显示成功提示
  success(content, duration = 3000) {
    return getMessageManager().success(content, duration)
  },

  // 手动隐藏消息（有淡出动画）
  hide() {
    getMessageManager().hide()
  },
  
  // 立即隐藏消息（无动画）
  hideImmediate() {
    getMessageManager().hideImmediate()
  },

  // 自定义消息
  show(options) {
    return getMessageManager().createMessage(options)
  },
  
  // 更新当前消息（如果存在）
  update(options) {
    return getMessageManager().updateMessage(options)
  }
}

export default TipMessage